/* iter179 — Field Schedule unfinished-billing banner.
   Polls /api/me/unfinished-billing?include_previous=true on mount + every 60s.
   - today_count > 0 → red banner "X jobs need Finish Billing"
   - prior_count > 0 → stronger red banner with extra warning
   - Clicking "Open list" expands an inline list of the unfinished jobs;
     clicking a job hands off to /employee/schedule?finish_billing=<id>.
*/
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, ChevronDown, ChevronUp, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UnfinishedBillingBanner = () => {
  const [data, setData] = useState({ today_count: 0, prior_count: 0, today: [], prior: [] });
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/me/unfinished-billing?include_previous=true`, { withCredentials: true });
      setData({
        today_count: d?.today_count || 0,
        prior_count: d?.prior_count || 0,
        today: d?.today || [],
        prior: d?.prior || [],
      });
    } catch {
      // tolerate auth issues silently — non-internal users see nothing
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const todayCount = data.today_count;
  const priorCount = data.prior_count;
  if (todayCount + priorCount === 0) return null;

  const all = [...data.prior, ...data.today];

  return (
    <div
      data-testid="unfinished-billing-banner"
      className="bg-red-500/10 border border-red-500/40 rounded-md p-3"
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 text-red-300 shrink-0" />
        <div className="flex-1 min-w-0">
          {priorCount > 0 ? (
            <>
              <p data-testid="prior-day-billing-headline" className="text-red-200 text-sm font-bold">
                You have {priorCount} job{priorCount === 1 ? '' : 's'} from a previous day with unfinished billing.
              </p>
              <p className="text-[#C9C9C9] text-xs">
                Please complete these before starting new customer jobs.
                {todayCount > 0 && <> Also {todayCount} from today.</>}
              </p>
            </>
          ) : (
            <p data-testid="today-billing-headline" className="text-red-200 text-sm font-bold">
              You have {todayCount} job{todayCount === 1 ? '' : 's'} that {todayCount === 1 ? 'needs' : 'need'} Finish Billing.
            </p>
          )}
        </div>
        <button
          type="button"
          data-testid="unfinished-billing-toggle"
          onClick={() => setExpanded((v) => !v)}
          className="text-red-200 hover:text-white text-xs underline flex items-center gap-1"
        >
          {expanded ? <><ChevronUp className="w-3 h-3" /> Hide</> : <><ChevronDown className="w-3 h-3" /> Open list</>}
        </button>
      </div>
      {expanded && (
        <div data-testid="unfinished-billing-list" className="mt-3 space-y-1.5">
          {all.map((j) => (
            <a
              key={j.id}
              href={`/employee/schedule?finish_billing=${j.id}`}
              data-testid={`unfinished-banner-job-${j.id}`}
              className="block bg-[#0A0A0A] border border-[#262626] hover:border-red-500/60 rounded p-2.5"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className="text-white text-sm font-bold truncate">
                    {j.customer_name || '(no customer)'} — {j.shift_title || j.service_type || '—'}
                  </p>
                  <p className="text-[#737373] text-[11px]">{j.date} {j.start_time && <>· {j.start_time}</>}</p>
                </div>
                <span className="text-red-300 text-[11px] inline-flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Finish Billing
                </span>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default UnfinishedBillingBanner;
